import { useState, useEffect } from "react";
import { ArrowLeft, Loader2, Copy, BadgeCheck, Calendar, Clock, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface PatternLane {
  key: string;
  originCity: string;
  originState: string;
  destCity: string;
  destState: string;
  equipment: string;
  /** How many times they posted on this specific weekday */
  dayHits: number;
  /** Total posts for this lane (all days) */
  totalPosts: number;
  /** The dominant weekday index */
  dayOfWeek: number;
  /** Consecutive weeks streak on this day */
  streak: number;
  avgRate: number | null;
  lastLoadId: string;
  lastPostedAt: string;
  hasTemplate: boolean;
  /** Average hour the user typically posts */
  avgHour: number;
}

interface PostDuplicatesPaneProps {
  onBack: () => void;
}

export function PostDuplicatesPane({ onBack }: PostDuplicatesPaneProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lanes, setLanes] = useState<PatternLane[]>([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().getDay();

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const analyze = async () => {
      try {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 120); // 4 months of history

        const [loadsRes, templatesRes] = await Promise.all([
          supabase
            .from("loads")
            .select("id, origin_city, origin_state, destination_city, destination_state, equipment_type, rate_amount, posted_at, created_at")
            .eq("created_by", user.id)
            .gte("posted_at", cutoff.toISOString())
            .order("posted_at", { ascending: false })
            .limit(500),
          supabase
            .from("load_templates" as any)
            .select("origin_city, origin_state, destination_city, destination_state, equipment_type")
            .eq("created_by", user.id)
            .eq("is_active", true),
        ]);

        const loads = loadsRes.data || [];
        const templates = (templatesRes.data as any[]) || [];

        const templateKeys = new Set(
          templates.map((t: any) =>
            `${t.origin_city}|${t.origin_state}|${t.destination_city}|${t.destination_state}|${t.equipment_type}`.toLowerCase()
          )
        );

        // Group loads by lane+equipment, tracking posting dates
        const groups = new Map<string, {
          originCity: string; originState: string;
          destCity: string; destState: string;
          equipment: string;
          postDates: Date[];
          rates: number[];
          lastLoadId: string;
        }>();

        for (const l of loads) {
          const key = `${l.origin_city}|${l.origin_state}|${l.destination_city}|${l.destination_state}|${l.equipment_type}`.toLowerCase();
          const postDate = new Date(l.posted_at || l.created_at || "");
          const existing = groups.get(key);
          if (existing) {
            existing.postDates.push(postDate);
            if (l.rate_amount) existing.rates.push(Number(l.rate_amount));
          } else {
            groups.set(key, {
              originCity: l.origin_city,
              originState: l.origin_state,
              destCity: l.destination_city,
              destState: l.destination_state,
              equipment: l.equipment_type,
              postDates: [postDate],
              rates: l.rate_amount ? [Number(l.rate_amount)] : [],
              lastLoadId: l.id,
            });
          }
        }

        // Analyze day-of-week patterns
        const results: PatternLane[] = [];

        for (const [key, g] of groups.entries()) {
          if (g.postDates.length < 1) continue;

          // Count posts per day of week
          const dayCounts = [0, 0, 0, 0, 0, 0, 0];
          const dayHours: number[][] = [[], [], [], [], [], [], []];
          for (const d of g.postDates) {
            dayCounts[d.getDay()]++;
            dayHours[d.getDay()].push(d.getHours());
          }

          // Find the dominant day
          let bestDay = 0;
          let bestCount = 0;
          for (let i = 0; i < 7; i++) {
            if (dayCounts[i] > bestCount) {
              bestCount = dayCounts[i];
              bestDay = i;
            }
          }

          // Calculate consecutive-week streak for the dominant day
          const postsOnDay = g.postDates
            .filter(d => d.getDay() === bestDay)
            .sort((a, b) => b.getTime() - a.getTime()); // newest first

          let streak = 1;
          if (bestCount >= 2) {
            for (let i = 0; i < postsOnDay.length - 1; i++) {
              const diffDays = (postsOnDay[i].getTime() - postsOnDay[i + 1].getTime()) / (1000 * 60 * 60 * 24);
              if (diffDays >= 5 && diffDays <= 9) { // roughly 1 week apart
                streak++;
              } else {
                break;
              }
            }
          }

          const avgHour = dayHours[bestDay].length > 0
            ? Math.round(dayHours[bestDay].reduce((a, b) => a + b, 0) / dayHours[bestDay].length)
            : 8;

          results.push({
            key,
            originCity: g.originCity,
            originState: g.originState,
            destCity: g.destCity,
            destState: g.destState,
            equipment: g.equipment,
            dayHits: bestCount,
            totalPosts: g.postDates.length,
            dayOfWeek: bestDay,
            streak,
            avgRate: g.rates.length > 0 ? Math.round(g.rates.reduce((a, b) => a + b, 0) / g.rates.length) : null,
            lastLoadId: g.lastLoadId,
            lastPostedAt: postsOnDay[0]?.toISOString() || "",
            hasTemplate: templateKeys.has(key),
            avgHour,
          });
        }

        // Sort: today+strong streak first, then other strong streaks, then recent lanes
        results.sort((a, b) => {
          const aStrong = a.streak >= 2 ? 1 : 0;
          const bStrong = b.streak >= 2 ? 1 : 0;
          if (aStrong !== bStrong) return bStrong - aStrong;
          if (aStrong && bStrong) {
            const aToday = a.dayOfWeek === today ? 1 : 0;
            const bToday = b.dayOfWeek === today ? 1 : 0;
            if (aToday !== bToday) return bToday - aToday;
            return b.streak - a.streak;
          }
          // Recent lanes: sort by last posted date
          return new Date(b.lastPostedAt).getTime() - new Date(a.lastPostedAt).getTime();
        });

        setLanes(results.slice(0, 12));
      } catch (err) {
        console.error("PostDuplicatesPane error:", err);
      } finally {
        setLoading(false);
      }
    };

    analyze();
  }, [user, today]);

  const formatHour = (h: number) => {
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 || 12;
    return `~${hr} ${ampm}`;
  };

  const strongLanes = lanes.filter(l => l.streak >= 2);
  const recentLanes = lanes.filter(l => l.streak < 2);
  const todayLanes = strongLanes.filter(l => l.dayOfWeek === today);
  const otherLanes = strongLanes.filter(l => l.dayOfWeek !== today);

  return (
    <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-3 py-2.5 bg-muted/20">
        <button onClick={onBack} className="p-1 rounded hover:bg-muted transition-colors">
          <ArrowLeft className="h-4 w-4 text-muted-foreground" />
        </button>
        <Sparkles className="h-4 w-4 text-primary" />
        <h4 className="text-caption font-bold text-foreground">ATLAS Pattern Detection</h4>
        {todayLanes.length > 0 && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary animate-pulse">
            {todayLanes.length} ready for today
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Analyzing your posting patterns…</span>
          </div>
        ) : lanes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-2">
            <Sparkles className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No lanes detected yet.</p>
            <p className="text-xs text-muted-foreground/70">
              Post loads and ATLAS will learn your lane patterns over time.
            </p>
          </div>
        ) : (
          <div>
            {/* Today's matches — highlighted */}
            {todayLanes.length > 0 && (
              <div className="bg-primary/[0.03]">
                <div className="px-3 py-2 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[11px] font-bold text-primary uppercase tracking-wider">
                    It's {DAY_NAMES[today]} — You usually post these
                  </span>
                </div>
                <ColumnHeader />
                {todayLanes.map((lane, i) => (
                  <LanePatternRow key={lane.key} lane={lane} isToday formatHour={formatHour} navigate={navigate} index={i} />
                ))}
              </div>
            )}

            {/* Other detected patterns */}
            {otherLanes.length > 0 && (
              <div>
                <div className="px-3 py-2 flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                    Other weekly patterns detected
                  </span>
                </div>
                <ColumnHeader />
                {otherLanes.map((lane, i) => (
                  <LanePatternRow key={lane.key} lane={lane} isToday={false} formatHour={formatHour} navigate={navigate} index={i} />
                ))}
              </div>
            )}

            {/* Recent lanes (no strong pattern yet) */}
            {recentLanes.length > 0 && (
              <div>
                <div className="px-3 py-2 flex items-center gap-1.5">
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                    Recently posted lanes
                  </span>
                </div>
                <ColumnHeader />
                {recentLanes.map((lane, i) => (
                  <LanePatternRow key={lane.key} lane={lane} isToday={false} formatHour={formatHour} navigate={navigate} index={i} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

function ColumnHeader() {
  return (
    <div className="hidden sm:grid grid-cols-[1fr_70px_60px_90px_90px] items-center px-3 py-1 border-b border-border/50">
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Lane</span>
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Posted</span>
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Last</span>
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Avg Rate</span>
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Action</span>
    </div>
  );
}

function LanePatternRow({
  lane,
  isToday,
  formatHour,
  navigate,
  index,
}: {
  lane: PatternLane;
  isToday: boolean;
  formatHour: (h: number) => string;
  navigate: ReturnType<typeof import("react-router-dom").useNavigate>;
  index: number;
}) {
  return (
    <div className={`grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_70px_60px_90px_90px] items-center gap-2 sm:gap-0 px-3 py-2 border-b border-border/30 hover:bg-muted/30 transition-colors ${index % 2 === 1 ? "bg-muted/20" : ""} ${isToday ? "bg-primary/[0.02]" : ""}`}>
      {/* Lane info */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground min-w-0">
          <span className="truncate">{lane.originCity}, {lane.originState}</span>
          <span className="text-muted-foreground flex-shrink-0">→</span>
          <span className="truncate">{lane.destCity}, {lane.destState}</span>
          {lane.hasTemplate && (
            <span title="Template saved"><BadgeCheck className="h-3.5 w-3.5 text-primary flex-shrink-0" /></span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
          <span className="text-[10px] text-muted-foreground">{lane.equipment}</span>
          {lane.streak >= 2 ? (
            <>
              <span className="text-[10px] text-muted-foreground">•</span>
              <span className="text-[10px] font-medium text-muted-foreground">
                Every {DAY_NAMES[lane.dayOfWeek]} {formatHour(lane.avgHour)}
              </span>
              <span className="text-[10px] text-muted-foreground">•</span>
              <span className="text-[10px] font-medium text-foreground/70">
                {lane.streak}wk streak
              </span>
            </>
          ) : (
            <>
              <span className="text-[10px] text-muted-foreground">•</span>
              <span className="text-[10px] font-medium text-muted-foreground">Recently posted</span>
            </>
          )}
          {/* Mobile-only inline metrics */}
          <span className="sm:hidden text-[10px] text-muted-foreground">•</span>
          <span className="sm:hidden text-[10px] font-medium text-foreground/70 tabular-nums">
            {lane.totalPosts}× posts
          </span>
          {lane.lastPostedAt && (
            <>
              <span className="sm:hidden text-[10px] text-muted-foreground">•</span>
              <span className="sm:hidden text-[10px] font-medium text-muted-foreground">
                {timeAgo(lane.lastPostedAt)}
              </span>
            </>
          )}
          {lane.avgRate && (
            <>
              <span className="sm:hidden text-[10px] text-muted-foreground">•</span>
              <span className="sm:hidden text-[10px] font-semibold text-primary tabular-nums">
                ${lane.avgRate.toLocaleString()}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Posted count (desktop) */}
      <span className="hidden sm:inline text-[10px] font-medium text-foreground/70 tabular-nums text-right">
        {lane.totalPosts}×
      </span>

      {/* Last posted (desktop) */}
      <span className="hidden sm:inline text-[10px] font-medium text-muted-foreground text-right">
        {lane.lastPostedAt ? timeAgo(lane.lastPostedAt) : "—"}
      </span>

      {/* Avg rate (desktop) */}
      <span className="hidden sm:inline text-[10px] font-semibold text-primary tabular-nums text-right">
        {lane.avgRate ? `$${lane.avgRate.toLocaleString()}` : "—"}
      </span>

      {/* Action */}
      <div className="flex justify-end">
        <Button
          size="sm"
          variant={isToday ? "default" : "outline"}
          className="h-6 text-[10px] px-2.5 flex-shrink-0"
          onClick={() => navigate(`/post-load?duplicate=${lane.lastLoadId}`)}
        >
          {isToday ? "Repost" : "Post"}
        </Button>
      </div>
    </div>
  );
}
